import React from "react";

/**
 * Line-icon set for PABS. All icons share a 24px grid, 1.75 stroke, and
 * `currentColor` so they inherit from whatever surface they sit on.
 */

export type IconProps = {
  size?: number;
  className?: string;
  title?: string;
  strokeWidth?: number;
};

const Icon = ({
  size = 24,
  className,
  title,
  strokeWidth = 1.75,
  children,
}: React.PropsWithChildren<IconProps>) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    role={title ? "img" : "presentation"}
    aria-hidden={title ? undefined : true}
    aria-label={title}
    focusable="false"
  >
    {title ? <title>{title}</title> : null}
    {children}
  </svg>
);

/* ---- Services ---------------------------------------------------------- */

export const IconFood = (p: IconProps) => (
  <Icon {...p}>
    <path d="M5 3v7a3 3 0 0 0 6 0V3" />
    <path d="M8 10v11" />
    <path d="M18.5 3c-1.5 1.6-2.5 3.9-2.5 6.3 0 1.6.9 2.7 2.5 2.7H19V3h-.5Z" />
    <path d="M18.5 12v9" />
  </Icon>
);

export const IconGrocery = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 8h16l-1.3 10.2a2 2 0 0 1-2 1.8H7.3a2 2 0 0 1-2-1.8L4 8Z" />
    <path d="M8.5 8V6a3.5 3.5 0 0 1 7 0v2" />
    <path d="M9.5 12v4M14.5 12v4" />
  </Icon>
);

export const IconErrand = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3 17h2l1.6-6.4A2 2 0 0 1 8.5 9H14l1.6 8" />
    <circle cx="6.5" cy="18.5" r="2.5" />
    <circle cx="17.5" cy="18.5" r="2.5" />
    <path d="M14 9V5.5a1.5 1.5 0 0 1 1.5-1.5H18" />
    <path d="M9 17h6" />
  </Icon>
);

/** Passenger ride (Angkas) — motorcycle with a helmeted rider. */
export const IconRide = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="5" cy="17.5" r="3" />
    <circle cx="19" cy="17.5" r="3" />
    <path d="M8 17.5h8" />
    <path d="M5 17.5 9.5 11h5l-2-3.5" />
    <path d="M14.5 11 19 17.5" />
    <path d="M12.5 7.5h3" />
    <circle cx="17" cy="5" r="2" />
  </Icon>
);

/* ---- Product / UI ------------------------------------------------------ */

export const IconPin = (p: IconProps) => (
  <Icon {...p}>
    <path d="M20 10.5c0 5.5-8 11-8 11s-8-5.5-8-11a8 8 0 0 1 16 0Z" />
    <circle cx="12" cy="10.5" r="2.75" />
  </Icon>
);

export const IconRoute = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="5.5" cy="5.5" r="2.5" />
    <circle cx="18.5" cy="18.5" r="2.5" />
    <path d="M8 5.5h5.5A3.5 3.5 0 0 1 17 9v0a3.5 3.5 0 0 1-3.5 3.5h-3A3.5 3.5 0 0 0 7 16v0a3.5 3.5 0 0 0 3.5 3.5H16" />
  </Icon>
);

export const IconWallet = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3 8.5A2.5 2.5 0 0 1 5.5 6H18a2 2 0 0 1 2 2v1" />
    <path d="M3 8.5V17a2.5 2.5 0 0 0 2.5 2.5H19a2 2 0 0 0 2-2V11a2 2 0 0 0-2-2H5.5A2.5 2.5 0 0 1 3 8.5Z" />
    <circle cx="16.5" cy="14" r="1.25" fill="currentColor" stroke="none" />
  </Icon>
);

export const IconReceipt = (p: IconProps) => (
  <Icon {...p}>
    <path d="M5 3.5h14v17l-2.3-1.5-2.35 1.5L12 19l-2.35 1.5L7.3 19 5 20.5v-17Z" />
    <path d="M9 8.5h6M9 12.5h6" />
  </Icon>
);

export const IconShield = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 2.75 4.5 6v6c0 4.6 3.2 8.4 7.5 9.25 4.3-.85 7.5-4.65 7.5-9.25V6L12 2.75Z" />
    <path d="m9 12 2.2 2.2L15.5 10" />
  </Icon>
);

export const IconBolt = (p: IconProps) => (
  <Icon {...p}>
    <path d="M13.5 2.5 5 13.5h6l-.5 8 8.5-11h-6l.5-8Z" />
  </Icon>
);

export const IconStore = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 9.5V19a1.5 1.5 0 0 0 1.5 1.5h13A1.5 1.5 0 0 0 20 19V9.5" />
    <path d="M3 6.5 4.5 3.5h15L21 6.5a2.5 2.5 0 0 1-4.5 1.5 2.5 2.5 0 0 1-4.5 0 2.5 2.5 0 0 1-4.5 0A2.5 2.5 0 0 1 3 6.5Z" />
    <path d="M9.5 20.5v-5h5v5" />
  </Icon>
);

export const IconChart = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 20h16" />
    <path d="M7 20v-6M12 20V6M17 20v-9" />
  </Icon>
);

export const IconChat = (p: IconProps) => (
  <Icon {...p}>
    <path d="M20.5 12c0 4.1-3.8 7.4-8.5 7.4-1.05 0-2.05-.16-2.97-.46L4 20.5l1.7-3.7C4.36 15.5 3.5 13.85 3.5 12 3.5 7.9 7.3 4.6 12 4.6s8.5 3.3 8.5 7.4Z" />
  </Icon>
);

export const IconClock = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5.2l3.2 2" />
  </Icon>
);

export const IconMoon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M20 14.2A8.2 8.2 0 0 1 9.8 4 8.5 8.5 0 1 0 20 14.2Z" />
  </Icon>
);

export const IconSparkle = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 3.5 13.7 9l5.5 1.7-5.5 1.7L12 18l-1.7-5.6L4.8 10.7 10.3 9 12 3.5Z" />
    <path d="M18.5 3.5v3M20 5h-3" />
  </Icon>
);

export const IconPhone = (p: IconProps) => (
  <Icon {...p}>
    <rect x="6.5" y="2.5" width="11" height="19" rx="2.5" />
    <path d="M10.5 5.5h3" />
    <path d="M11 18.5h2" />
  </Icon>
);

/* ---- Utility ----------------------------------------------------------- */

export const IconArrowRight = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4.5 12h15" />
    <path d="m13.5 6 6 6-6 6" />
  </Icon>
);

export const IconChevronDown = (p: IconProps) => (
  <Icon {...p}>
    <path d="m6 9.5 6 6 6-6" />
  </Icon>
);

export const IconCheck = (p: IconProps) => (
  <Icon {...p}>
    <path d="m5 12.5 4.5 4.5L19 7" />
  </Icon>
);

export const IconSearch = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="11" cy="11" r="7" />
    <path d="m16.5 16.5 4 4" />
  </Icon>
);

export const IconClose = (p: IconProps) => (
  <Icon {...p}>
    <path d="M6 6l12 12M18 6 6 18" />
  </Icon>
);

export const IconMenu = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 7h16M4 12h16M4 17h16" />
  </Icon>
);

export const IconLink = (p: IconProps) => (
  <Icon {...p}>
    <path d="M10 13.5a3.5 3.5 0 0 0 5 0l3-3a3.54 3.54 0 0 0-5-5l-1.5 1.5" />
    <path d="M14 10.5a3.5 3.5 0 0 0-5 0l-3 3a3.54 3.54 0 0 0 5 5L12.5 17" />
  </Icon>
);

export const IconMail = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3" y="5" width="18" height="14" rx="2.5" />
    <path d="m4 7.5 7.1 5a1.5 1.5 0 0 0 1.8 0l7.1-5" />
  </Icon>
);

/* ---- Brand marks (platform logos, filled) ------------------------------ */

export const MarkApple = ({ size = 22 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.6 12.9c0-2.6 2.1-3.9 2.2-4-1.2-1.8-3.1-2-3.8-2-1.6-.2-3.1 1-3.9 1s-2.1-.9-3.4-.9c-1.8 0-3.4 1-4.3 2.6-1.8 3.2-.5 7.9 1.3 10.5.9 1.3 1.9 2.7 3.3 2.6 1.3-.05 1.8-.85 3.4-.85s2 .85 3.4.8c1.4 0 2.3-1.3 3.2-2.6.7-1 1-2 1-2-.05 0-2.4-.9-2.4-3.2Z" />
    <path d="M14.3 4.9c.7-.9 1.2-2.1 1.05-3.4-1.05.05-2.3.7-3.05 1.6-.65.8-1.25 2.05-1.1 3.25 1.15.1 2.35-.6 3.1-1.45Z" />
  </svg>
);

export const MarkGooglePlay = ({ size = 22 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M4.2 2.9c-.3.3-.45.75-.45 1.35v15.5c0 .6.15 1.05.45 1.35l.1.1 8.7-8.7v-.2L4.3 2.8l-.1.1Z" />
    <path d="m16 15.4-2.9-2.9v-.2l2.9-2.9.1.05 3.45 1.95c1 .55 1 1.5 0 2.05L16.1 15.35l-.1.05Z" />
    <path d="M16.1 15.35 13.1 12.35 4.2 21.2c.35.35.9.4 1.55.05l10.35-5.9Z" />
    <path d="M16.1 8.65 5.75 2.75c-.65-.35-1.2-.3-1.55.05l8.9 8.85 3-3Z" />
  </svg>
);
